import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Table,
  InputGroup,
  InputGroupText,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Modal,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/Common/Breadcrumbs";
import { customerReturnActions } from "../../sagas/customerReturnSlice";
import { CUSTOMER_RETURNS_PAGE } from "../../routes/routeConstants";
import { debounce, initial, set } from "lodash";

// Import actions from other slices
import { clientActions } from "../../sagas/clientSlice";
import { invoiceActions } from "../../sagas/invoiceSlice";
import { stockActions } from "../../sagas/stockSlice";
import { formatPrice, preventScroll } from "../../helpers/utility";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { productActions } from "../../sagas/productSlice";
import { FaBarcode, FaBox, FaBoxes, FaTag, FaTrash, FaUndo } from "react-icons/fa";
import Swal from "sweetalert2";
import { commercialActions } from "../../sagas/commercialSlice";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { transporteurActions } from "../../sagas/transporteurSlice";
import { companyActions } from "../../sagas/companySlice";
import "./CustomerReturns.css";

const CustomerReturnForm = ({ customerReturn, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(customerReturn || id);
  const { currentReturn, newReturnNumber, loading } = useSelector((state) => state.customerReturns);
  const activeReturn = customerReturn || (id ? currentReturn : null);
  const { list } = useSelector((state) => state.deliveryNotes);
  const [isSearching, setIsSearching] = useState(false);
  const { clients } = useSelector((state) => state.clients);
  const { invoices } = useSelector((state) => state.invoices);
  const [documentType, setDocumentType] = useState("NONE"); // NONE, FACTURE, BL
  const [chooseProductModal, setChooseProductModal] = useState(false);
  const [selectedInvoiceItems, setSelectedInvoiceItems] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unite
  const { list: commercialList, loading: commercialLoading } = useSelector((state) => state.commercials); // Adjust state path as needed
  const { entrepots } = useSelector(state => state.entrepots);
  const { transporteurs } = useSelector(state => state.transporteurs);
  const { companies } = useSelector(state => state.companies);
  const defaultCompanyId = useMemo(
    () => companies?.find((item) => item.isDefault)?.id ?? companies?.[0]?.id ?? null,
    [companies]
  );

  const canBeUpdated = (isEditing && activeReturn?.statut === "BROUILLON") || !isEditing;
  const toggleChooseProductModal = () => setChooseProductModal(!chooseProductModal);
  const {
    products,
    loading: productLoading,
    totalPages,
    totalElements,
    pageSize,
  } = useSelector((status) => status.products);

  useEffect(() => {
    // Load clients and products
    dispatch(clientActions.search({
      query: ""
    }));
    dispatch(productActions.findAllUnites());
    dispatch(entrepotActions.list());
    dispatch(transporteurActions.list());
    dispatch(companyActions.getAll({}));
    dispatch(commercialActions.list({ query: '' })); // Fetch commercials on component mount
    if (id) {
      dispatch(customerReturnActions.getById({ id }));
    }
    if (!isEditing) {
      dispatch(customerReturnActions.generateNumero())
    }
  }, [dispatch, id, isEditing]);


  useEffect(() => {
    dispatch(productActions.findAll({
      search: searchTerm,
      page: currentPage - 1,
      size: 10,
    }));

  }, [currentPage, chooseProductModal]);


  const handleClientChange = (clientId, setFieldValue) => {
    setFieldValue("clientId", clientId);
    setSelectedItems([]);
  };

  const clientOptions = clients?.map(client => ({
    value: client.id,
    label: client.typeClient === 'PARTICULIER'
      ? `${client.nom} ${client.prenom || ''} `
      : client.entreprise,
  })) ?? [];

  // Handle modal search input change
  const handleModalSearchChange = (e) => {
    const value = e.target.value;
    setModalSearchTerm(value);
    debouncedModalSearch(value);
  };

  const debouncedModalSearch = useCallback(
    debounce((query) => {
      setIsSearching(true);
      dispatch(productActions.findAll({
        search: query,
        page: 0,
        size: pageSize,
        onSuccess: () => setIsSearching(false)
      }));
      setIsSearching(false);
    }, 1000), // Wait 1 second after user stops typing
    [modalSearchTerm]
  );


  const validationSchema = Yup.object().shape({
    dateOperation: Yup.date().required(t("customerReturns.form.validation.dateRequired")),
    transporteurId: Yup.number().required(t('reception.transporteurMissing')),
    motifRetour: Yup.string().required(t("customerReturns.form.validation.reasonRequired")),
    lignes: Yup.array().min(1, t("customerReturns.form.validation.itemsRequired")),
  });


  const calculateTotals = (lines) => {
    let totalHT = 0;
    let totalTVA = 0;
    let totalTTC = 0;

    lines.forEach((line) => {
      const qte = Number(line.quantiteRetournee) || 0;
      const ttc = Number(line.prixUnitaireTTC) || 0;
      const taux = Number(line.tauxTVA) || 0;

      const lineTotalTTC = round2(qte * ttc);
      const lineHT =
        taux > 0 ? round2(lineTotalTTC / (1 + taux)) : lineTotalTTC;
      const lineTVA = round2(lineTotalTTC - lineHT);

      totalTTC += lineTotalTTC;
      totalHT += lineHT;
      totalTVA += lineTVA;
    });

    return {
      montantHT: round2(totalHT),
      montantTVA: round2(totalTVA),
      montantTTC: round2(totalTTC),
    };
  };


  const handleSubmit = (values, { setSubmitting }) => {
    const totals = calculateTotals(values.lignes);
    const dataToSubmit = {
      ...values,
      ...totals,
      isPassager: values.clientId ? false : true,
    };

    if (isEditing) {
      dispatch(
        customerReturnActions.update({
          id: activeReturn?.id,
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('common.success'),
              text: t('customerReturns.messages.updateSuccess'),
            });
            handleClose();
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('common.error'),
              text: t('customerReturns.messages.saveError'),
            });
            setSubmitting(false);
          },
        })
      );
    } else {
      dispatch(
        customerReturnActions.create({
          data: dataToSubmit,
          onSuccess: () => {
            Swal.fire({
              icon: 'success',
              title: t('common.success'),
              text: t('customerReturns.messages.updateSuccess'),
            });
            handleClose();
          },
          onError: (error) => {
            Swal.fire({
              icon: 'error',
              title: t('common.error'),
              text: t('customerReturns.messages.saveError'),
            });
            setSubmitting(false);
          },
        })
      );
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate("/sales-module/returns");
  };

  const commercialOptions = commercialList?.map(com => ({
    value: com.id,
    label: com.fullName,
  })) ?? [];

  const transporteurOptions = transporteurs?.map(transporteur => ({
    value: transporteur.id,
    label: transporteur.code + ' - ' + transporteur.libelle,
  })) ?? [];

  const companyOptions = companies?.map(company => ({
    value: company.id,
    label: company.name,
  })) ?? [];

  const initialValues = isEditing && activeReturn
    ? {
      numeroRetour: activeReturn.numeroRetour || "",
      dateOperation: activeReturn.dateOperation || new Date().toISOString().split("T")[0],
      clientId: activeReturn.clientId || null,
      companyId: activeReturn.companyId || defaultCompanyId,
      entrepotId: activeReturn.entrepotId,
      commercialId: activeReturn.commercialId || null,
      documentType: "NONE",
      motifRetour: activeReturn.motifRetour || "",
      modeRemboursement: "AVOIR",
      transporteurId: activeReturn.transporteurId,
      isPassager: activeReturn.passager || false,
      lignes: activeReturn.lignes?.map(line => ({
        articleId: line.articleId,
        articleLibelle: line.articleNom,
        articleCode: line.articleCode,
        quantiteRetournee: line.quantiteRetournee,
        quantiteOriginale: line.quantiteOriginale,
        prixUnitaireHT: line.prixUnitaireHT,
        prixUnitaireTTC: line.prixUnitaireTTC,
        tauxTVA: line.tauxTVA,
        unite: unites.find(u => u.code === line.unite)?.libelle
      })) || [],
    }
    : {
      numeroRetour: newReturnNumber,
      dateOperation: new Date().toISOString().split("T")[0],
      clientId: null,
      companyId: defaultCompanyId,
      entrepotId: null,
      commercialId: null,
      transporteurId: null,
      documentType: "NONE",
      motifRetour: "",
      modeRemboursement: "AVOIR",
      lignes: [],
    };


  if ((loading && isEditing) || (id && !activeReturn)) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center my-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  const entrepotOptions = entrepots?.map(entrepot => ({
    value: entrepot.id,
    label: entrepot.code + ' - ' + entrepot.libelle,
  })) ?? [];

  const round2 = (n) =>
    Math.round((Number(n) + Number.EPSILON) * 100) / 100;

  const recalcLineAmounts = (line) => {
    const qte = Number(line.quantiteRetournee) || 0;
    const prixTTC = Number(line.prixUnitaireTTC) || 0;
    const taux = Number(line.tauxTVA) || 0;

    const montantTTC = round2(qte * prixTTC);

    const montantHT =
      taux > 0 ? round2(montantTTC / (1 + taux)) : montantTTC;

    const montantTVA = round2(montantTTC - montantHT);

    return {
      ...line,
      montantHT,
      montantTVA,
      montantTTC,
    };
  };

  const handleArrowNavigation = (e, rowIndex, fieldName, totalRows) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();

      const direction = e.key === "ArrowUp" ? -1 : 1;
      const nextRow = rowIndex + direction;

      if (nextRow >= 0 && nextRow < totalRows) {
        const nextInput = document.querySelector(
          `[data-field="${fieldName}"][data-row="${nextRow}"]`
        );

        if (nextInput) {
          nextInput.focus();
          nextInput.select?.();
        }
      }
    }
  };



  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => {
              const totals = calculateTotals(values.lignes);
              return (
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col lg={12}>
                      <Card className="return-form-card">
                        <CardBody>
                          <div className="return-form-header">
                            <div>
                              <div className="return-form-title">
                                <FaUndo className="me-2" />
                                {isEditing ? t("customerReturns.edit") : t("customerReturns.new")}
                              </div>
                              <div className="return-form-subtitle">
                                {values.numeroRetour || newReturnNumber || "-"}
                              </div>
                            </div>
                            <div className="return-form-total">
                              <span>{t("customerReturns.montantTTC")}</span>
                              <strong>{formatPrice(totals.montantTTC)}</strong>
                            </div>
                          </div>
                          <div className="return-form-section">
                            <div className="return-section-title">
                              {t("customerReturns.form.information", "Informations")}
                            </div>
                            <Row className="g-3">
                              <Col md={3}>
                                <div className="mb-3">
                                  <label className="form-label">
                                    {t("customerReturns.form.fields.returnNumber")}
                                  </label>
                                  <Field
                                    name="numeroRetour"
                                    type="text"
                                    className="form-control"
                                    placeholder={t("customerReturns.form.placeholders.returnNumber")}
                                    disabled
                                  />
                                </div>
                              </Col>
                              <Col md={3}>
                                <Label htmlFor="companyId">{t('company.company', 'Entreprise')}:</Label>
                                <Select
                                  id="companyId"
                                  options={companyOptions}
                                  value={companyOptions.find(option => option.value === values.companyId)}
                                  onChange={(selectedOption) => {
                                    setFieldValue('companyId', selectedOption ? selectedOption.value : null);
                                  }}
                                  placeholder={t('company.company', 'Entreprise')}
                                  isSearchable
                                  isDisabled={!canBeUpdated}
                                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                  styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                  }}
                                />
                              </Col>
                              <Col md={3}>
                                <div className="mb-3">
                                  <label className="form-label">
                                    {t("customerReturns.form.fields.client")}
                                  </label>

                                <Select
                                  value={clientOptions.find(option => option.value === values.clientId)}
                                  onChange={(option) => handleClientChange(option.value, setFieldValue)}
                                  options={clientOptions}
                                  placeholder={t("customerReturns.form.placeholders.selectClient")}
                                  isDisabled={!canBeUpdated}
                                  isSearchable
                                  className={
                                    errors.client && touched.client ? "is-invalid" : ""
                                  }
                                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                  styles={{
                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                  }}
                                />

                                {errors.client && touched.client && (
                                  <div className="invalid-feedback d-block">
                                    {errors.client}
                                  </div>
                                )}
                              </div>
                            </Col>
                            <Col md="3">
                              <Label htmlFor="commercialId">{t('delivery.commercial')}:</Label>
                              <Select
                                id="commercialId"
                                options={commercialOptions}
                                value={commercialOptions.find(option => option.value === values.commercialId)}
                                onChange={(selectedOption) => {
                                  setFieldValue('commercialId', selectedOption.value);
                                }}
                                placeholder={t('delivery.selectCommercial')}
                                isSearchable
                                isDisabled={!canBeUpdated}
                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                styles={{
                                  menuPortal: base => ({ ...base, zIndex: 9999 })
                                }}
                              />
                              {errors.commercialId && touched.commercialId && (
                                <div className="text-danger small mt-1">{errors.commercialId}</div>
                              )}
                            </Col>
                            <Col md="3">
                              <Label htmlFor="transporteurId">{t('bonLivraison.transporteur')}:</Label>
                              <Select
                                id="transporteurId"
                                options={transporteurOptions}
                                value={transporteurOptions.find(option => option.value === values.transporteurId)}
                                onChange={(selectedOption) => setFieldValue('transporteurId', selectedOption.value)}
                                placeholder={t('bonLivraison.selectTransporteur')}
                                isSearchable
                                isDisabled={!canBeUpdated}
                              />
                              {touched.transporteurId && errors.transporteurId && (
                                <div className="invalid-feedback d-block">
                                  {errors.transporteurId}
                                </div>
                              )}
                            </Col>
                            <Col md={3}>
                              <div className="mb-3">
                                <label className="form-label">
                                  {t("customerReturns.form.fields.returnDate")}
                                </label>
                                <Field
                                  name="dateOperation"
                                  type="date"
                                  className={`form-control ${errors.dateOperation && touched.dateOperation ? "is-invalid" : ""
                                    }`}
                                  disabled={!canBeUpdated}
                                />
                                <ErrorMessage
                                  name="dateOperation"
                                  component="div"
                                  className="invalid-feedback"
                                />
                              </div>
                            </Col>
                            <Col md="3">
                              <Label htmlFor="entrepotId">{t('bonLivraison.entrepot')}:</Label>
                              <Select
                                id="entrepotId"
                                options={entrepotOptions}
                                value={entrepotOptions.find(option => option.value === values.entrepotId)}
                                onChange={(selectedOption) => setFieldValue('entrepotId', selectedOption.value)}
                                placeholder={t('bonLivraison.selectEntrepot')}
                                isSearchable
                                isDisabled={!canBeUpdated}
                              />
                              {errors.entrepotId && touched.entrepotId && (
                                <div className="text-danger small mt-1">{errors.entrepotId}</div>
                              )}
                            </Col>
                            <Col md={3}>
                              <Label htmlFor="reason">{t('customerReturns.form.fields.reason')}:</Label>
                              <Field
                                name="motifRetour"
                                type="text"
                                className={`form-control ${errors.motifRetour && touched.motifRetour ? "is-invalid" : ""}`}
                                placeholder={t("customerReturns.form.placeholders.reason")}
                                disabled={!canBeUpdated}
                              />

                            </Col>
                            </Row>
                          </div>

                          <div className="return-form-section return-lines-section">
                            <div className="return-section-toolbar">
                              <div>
                                <div className="return-section-title">
                                  {t("customerReturns.form.articles", "Articles")}
                                </div>
                                <div className="return-section-subtitle">
                                  {values.lignes.length} {t("customerReturns.form.columns.item", "Article")}
                                </div>
                              </div>
                              {documentType === "NONE" && (
                                <Button
                                  color="primary"
                                  onClick={toggleChooseProductModal}
                                  disabled={!canBeUpdated}
                                >
                                  <i className="bx bx-plus me-1"></i>
                                  {t("customerReturns.selectProduct")}
                                </Button>
                              )}
                            </div>
                            <Row>
                              <Col lg={12}>

                              {/* Manual item addition section for when no document is selected */}
                              <FieldArray name="lignes">
                                {({ remove, push }) => (
                                  <div className="table-responsive return-lines-table-wrap">
                                    <table className="table table-hover align-middle return-lines-table">
                                      <thead>
                                        <tr>
                                          <th>{t("customerReturns.form.columns.item")}</th>
                                          <th>{t("customerReturns.form.columns.returnedQte")}</th>
                                          <th>{t("customerReturns.form.columns.unite")}</th>
                                          <th>{t("customerReturns.form.columns.unitPrice")}</th>
                                          <th>{t("customerReturns.form.columns.total")}</th>
                                          <th>{t("customerReturns.form.columns.actions")}</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {values.lignes.length === 0 ? (
                                          <tr>
                                            <td colSpan="6" className="text-center">
                                              {t("customerReturns.form.noItems")}
                                            </td>
                                          </tr>
                                        ) : (
                                          values.lignes.map((line, index) => {
                                            const lineTotal = line.montantTTC || 0;
                                            return (
                                              <tr key={index}>
                                                <td>
                                                  {line.articleLibelle || "Article inconnu"}
                                                </td>
                                                <td>
                                                  <Field
                                                    name={`lignes.${index}.quantiteRetournee`}
                                                    type="number"
                                                    data-row={index}
                                                    data-field="quantiteRetournee"
                                                    onFocus={(e) => {
                                                      e.target.select();
                                                      e.target.addEventListener("wheel", preventScroll, { passive: false });
                                                    }}
                                                    onBlur={(e) => {
                                                      e.target.removeEventListener("wheel", preventScroll);
                                                    }}
                                                    onKeyDown={(e) =>
                                                      handleArrowNavigation(e, index, "quantiteRetournee", values.lignes.length)
                                                    }
                                                    className="form-control no-spinner"
                                                    style={{ width: "120px" }}
                                                    disabled={!canBeUpdated}
                                                    onChange={(e) => {
                                                      const qte = Number(e.target.value) || 0;
                                                      const updatedLine = recalcLineAmounts({
                                                        ...values.lignes[index],
                                                        quantiteRetournee: qte,
                                                      });
                                                      setFieldValue(`lignes.${index}`, updatedLine);
                                                    }}
                                                  />
                                                </td>
                                                <td>
                                                  {unites.find(u => u.code === line.unite)?.libelle || line.unite}
                                                </td>
                                                <td>
                                                  <InputGroup size="sm">
                                                    <Field
                                                      name={`lignes.${index}.prixUnitaireTTC`}
                                                      type="number"
                                                      step="0.01"
                                                      data-row={index}
                                                      data-field="prixUnitaireTTC"
                                                      onFocus={(e) => {
                                                        e.target.select();
                                                        e.target.addEventListener("wheel", preventScroll, { passive: false });
                                                      }}
                                                      onBlur={(e) => {
                                                        e.target.removeEventListener("wheel", preventScroll);
                                                      }}
                                                      onKeyDown={(e) =>
                                                        handleArrowNavigation(e, index, "prixUnitaireTTC", values.lignes.length)
                                                      }
                                                      className="form-control no-spinner"
                                                      style={{ width: "150px" }}
                                                      disabled={!canBeUpdated}
                                                      onChange={(e) => {
                                                        const prix = Number(e.target.value) || 0;
                                                        const updatedLine = recalcLineAmounts({
                                                          ...values.lignes[index],
                                                          prixUnitaireTTC: prix,
                                                        });
                                                        setFieldValue(`lignes.${index}`, updatedLine);
                                                      }}
                                                    />

                                                  </InputGroup>
                                                </td>
                                                <td>{formatPrice(lineTotal)}</td>
                                                <td>
                                                  <Button
                                                    color="danger"
                                                    size="sm"
                                                    onClick={() => remove(index)}
                                                    disabled={!canBeUpdated}
                                                  >
                                                    <FaTrash />
                                                  </Button>
                                                </td>
                                              </tr>
                                            );
                                          })
                                        )}
                                      </tbody>
                                      <tfoot>
                                        {values.lignes.length > 0 && (
                                          <>
                                            <tr>
                                              <th colSpan="4" className="text-end">
                                                {t("customerReturns.form.totals.totalHT")}:
                                              </th>
                                              <th>{formatPrice(totals.montantHT)}</th>
                                              <th />
                                            </tr>

                                            <tr>
                                              <th colSpan="4" className="text-end">
                                                {t("customerReturns.form.totals.totalVAT")}:
                                              </th>
                                              <th>{formatPrice(totals.montantTVA)}</th>
                                              <th />
                                            </tr>

                                            <tr>
                                              <th colSpan="4" className="text-end">
                                                {t("customerReturns.form.totals.totalTTC")}:
                                              </th>
                                              <th>{formatPrice(totals.montantTTC)}</th>
                                              <th />
                                            </tr>
                                          </>
                                        )}
                                      </tfoot>

                                    </table>
                                  </div>
                                )}
                              </FieldArray>
                              {errors.lignes && typeof errors.lignes === "string" && (
                                <div className="text-danger mt-2">{errors.lignes}</div>
                              )}

                              </Col>
                            </Row>
                          </div>

                        </CardBody>
                      </Card>
                    </Col>
                  </Row>


                  <Row className="return-form-actions-row">
                    <Col>
                      <div className="return-form-actions">
                        <Button
                          color="secondary"
                          onClick={handleClose}
                        >
                          {t("customerReturns.form.buttons.cancel")}
                        </Button>
                        <Button color="primary" type="submit" disabled={isSubmitting || !canBeUpdated}>
                          {isSubmitting ? (
                            <>
                              <Spinner size="sm" className="me-2" />{" "}
                              {t("customerReturns.form.buttons.saving")}
                            </>
                          ) : (
                            t("customerReturns.form.buttons.save")
                          )}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  <Modal isOpen={chooseProductModal} toggle={toggleChooseProductModal} size="xl">
                    <ModalHeader toggle={toggleChooseProductModal}>
                      {t('product.chooseProduct')}
                    </ModalHeader>
                    <ModalBody>
                      <Row>
                        <Col md={12}>
                          <Input
                            type="text"
                            placeholder={t('product.searchPlaceholder')}
                            value={modalSearchTerm}
                            onChange={handleModalSearchChange}
                            className="me-2" // Added margin to the right
                          />
                        </Col>

                      </Row>
                      <>
                        <div className="cart-container" style={{
                          height: '300px',
                          overflowY: 'auto'
                        }}>

                          <Table hover bordered className="align-middle mb-0">
                            <thead className="bg-light">
                              <tr>
                                <th className="align-middle">
                                  <FaBarcode className="me-2 text-primary" size={12} />
                                  {t('product.code')}
                                </th>
                                <th className="align-middle">
                                  <FaBox className="me-2 text-primary" size={12} />
                                  {t('product.description')}
                                </th>
                                <th className="align-middle text-end">
                                  <FaTag className="me-2 text-primary" size={12} />
                                  {t('product.salePrice')}
                                </th>
                                {/* <th className="align-middle text-end">
                                  <FaTag className="me-2 text-primary" size={12} />
                                  {t('product.buyingPrice')}
                              </th> */}
                                <th className="align-middle text-center">
                                  <FaBoxes className="me-2 text-primary" size={12} />
                                  {t('product.stockPhysique')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {!products || products.length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="text-center py-4">
                                    <div className="text-muted">
                                      {t('product.noProducts')}
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                products.map((product) => (
                                  <tr
                                    key={product.id}
                                    onClick={() => {
                                      // Check if the product is already in the return lines
                                      const isProductAlreadyAdded = values.lignes.some(
                                        line => (line.article === product.id || line.articleId === product.id)
                                      );

                                      if (isProductAlreadyAdded) {
                                        // Show a warning toast
                                        toast.warning(t("customerReturns.messages.itemAlreadyAdded"));
                                      } else {
                                        // Add product to return lines
                                        const newLine = recalcLineAmounts({
                                          article: product.id,
                                          articleId: product.id, // Keep both for compatibility
                                          articleLibelle: product.libelle,
                                          quantiteRetournee: 1,
                                          quantiteOriginale: 1,
                                          prixUnitaireHT: product.prixVenteHT || (product.prixVenteTTC1 / (1 + (product.tauxTVA || 0.2))),
                                          prixUnitaireTTC: product.prixVenteTTC1,
                                          tauxTVA: product.tauxTVA || 0.2,
                                          unite: unites.find(u => u.code === product.unite)?.libelle,
                                        });
                                        setFieldValue("lignes", [...values.lignes, newLine]);
                                        toggleChooseProductModal();
                                      }
                                    }}
                                    style={{
                                      cursor: 'pointer',
                                      backgroundColor: values.lignes.some(
                                        line => (line.article === product.id || line.articleId === product.id)
                                      ) ? '#f8f9fa' : 'inherit'
                                    }}
                                    className={values.lignes.some(
                                      line => (line.article === product.id || line.articleId === product.id)
                                    ) ? 'text-muted' : ''}
                                  >
                                    <td className="align-middle">{product.code}</td>
                                    <td className="align-middle">
                                      {product.libelle}
                                      {values.lignes.some(
                                        line => (line.article === product.id || line.articleId === product.id)
                                      ) && (
                                          <span className="ms-2 badge bg-light text-dark">
                                            {t("customerReturns.form.alreadyAdded")}
                                          </span>
                                        )}
                                    </td>
                                    <td className="align-middle text-end">
                                      {formatPrice(product.prixVenteTTC1)}
                                    </td>
                                    <td className="align-middle text-center">{product.stockPhysique}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <small className="text-muted">
                            {t('product.showing', {
                              start: (currentPage - 1) * pageSize + 1,
                              end: Math.min(currentPage * pageSize, totalElements),
                              total: totalElements,
                              currentPage: currentPage,
                              totalPages: totalPages
                            })}
                          </small>
                        </div>
                      </>
                    </ModalBody>
                    <ModalFooter>
                      <Button color="secondary" onClick={() => {
                        setModalSearchTerm('');
                        setCurrentPage(1);
                        toggleChooseProductModal()
                      }}>
                        {t('common.cancel')}
                      </Button>
                    </ModalFooter>
                  </Modal>
                </Form>
              )
            }}
          </Formik>
        </Container>
      </div>

    </React.Fragment>
  );
};

export default CustomerReturnForm;


